// ============================================================================
//  BulkInvite.jsx — /manage/invite. Onboard a whole cohort in one pass.
//
//  Paste "Name, email" lines, pick the team and role, preview, send. The
//  parser is deliberately forgiving because the list is going to arrive from a
//  spreadsheet, a phone note, or an email thread — not from a form.
//
//  Nothing is sent until the preview has been seen. The Edge Function paces
//  the sends itself and returns a per-row result, so a partial failure is
//  reported honestly rather than thrown away.
// ============================================================================

import React, { useMemo, useState } from "react";
import { Users, Send, ArrowLeft, Check, AlertTriangle, Copy } from "lucide-react";
import { C, display, kicker, lbl, inp, primaryBtn, smallBtn, smallGhost } from "../theme/tokens.js";
import { Pill } from "../theme/primitives.jsx";
import { grantableRoles, label as roleLabel, capacityOf } from "../lib/roles.js";
import { inviteMany } from "../lib/storage.js";

const CHUNK = 25;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * Tolerates: "Name, email" · "Name <email>" · tab-separated (spreadsheet
 * paste) · semicolons · a bare email (name derived from the local part) ·
 * quoted names · trailing commas · blank lines.
 */
export function parseRoster(text) {
  const rows = [];
  const seen = new Set();

  for (const rawLine of (text ?? "").split(/\r?\n/)) {
    const line = rawLine.trim().replace(/,+$/, "");
    if (!line) continue;

    let name = "";
    let email = "";

    const angle = line.match(/^(.*?)<([^>]+)>$/);
    if (angle) {
      name = angle[1];
      email = angle[2];
    } else {
      const parts = line.split(/\t|;|,/).map((s) => s.trim()).filter(Boolean);
      const emailPart = parts.find((p) => EMAIL_RE.test(p));
      if (emailPart) {
        email = emailPart;
        name = parts.filter((p) => p !== emailPart).join(" ");
      } else {
        email = parts[parts.length - 1] ?? line;
        name = parts.slice(0, -1).join(" ");
      }
    }

    name = name.replace(/^["']|["']$/g, "").trim();
    email = email.trim().toLowerCase();
    if (!name && EMAIL_RE.test(email)) {
      // "jsmith@x.com" -> "Jsmith". A rough guess he can fix in his profile,
      // which still beats an empty name on the roster.
      const local = email.split("@")[0].replace(/[._-]+/g, " ");
      name = local.replace(/\b\w/g, (c) => c.toUpperCase());
    }

    const valid = EMAIL_RE.test(email);
    const duplicate = valid && seen.has(email);
    if (valid) seen.add(email);

    rows.push({
      raw: line, name, email, valid, duplicate,
      problem: !valid ? "Not a valid email address"
             : duplicate ? "Listed more than once"
             : null,
    });
  }
  return rows;
}

export default function BulkInvite({ st, me, onBack, onChanged }) {
  const led = st.myLedGroupIds ?? [];
  const isSuper = me?.role === "super_admin";
  const teamIds = Object.keys(st.groups ?? {}).filter((id) => isSuper || led.includes(id));

  const [groupId, setGroupId] = useState(teamIds[0] ?? "");
  const [role, setRole] = useState("mentee");
  const [text, setText] = useState("");
  const [results, setResults] = useState(null);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");

  const grantable = grantableRoles(me);
  const rows = useMemo(() => parseRoster(text), [text]);
  const cap = groupId ? capacityOf(st.people, st.groups, groupId) : null;

  const sendable = rows.filter((r) => r.valid && !r.duplicate);
  const overflow = cap ? Math.max(0, sendable.length - cap.remaining) : 0;
  const willSend = cap ? Math.min(sendable.length, cap.remaining) : sendable.length;

  async function send() {
    setSending(true);
    setErr("");
    const all = [];
    try {
      for (let i = 0; i < sendable.length; i += CHUNK) {
        const batch = sendable.slice(i, i + CHUNK)
          .map((r) => ({ email: r.email, full_name: r.name }));
        const res = await inviteMany({ people: batch, group_id: groupId, role });
        all.push(...(res?.results ?? []));
      }
      setResults(all);
      await onChanged();
    } catch (e) {
      setErr(e?.message || "The invites could not be sent.");
    } finally {
      setSending(false);
    }
  }

  function copySummary() {
    const lines = (results ?? []).map(
      (r) => `${r.ok ? "SENT   " : "FAILED "} ${r.email}${r.ok ? "" : "  — " + (r.error ?? r.code)}`
    );
    navigator.clipboard?.writeText(lines.join("\n"));
  }

  return (
    <div style={{ maxWidth: 820, margin: "0 auto", padding: "0 24px 80px" }}>
      <button onClick={onBack} style={{ ...smallGhost, marginTop: 26 }}>
        <ArrowLeft size={14} /> Back to Manage
      </button>

      <div style={{ padding: "14px 0 20px" }}>
        <div style={kicker}>New cohort</div>
        <h1 style={{ ...display, fontSize: 34, margin: "8px 0 6px", color: C.text }}>
          Invite a cohort
        </h1>
        <p style={{ fontSize: 14, color: C.sub, maxWidth: 600, lineHeight: 1.6 }}>
          Paste one man per line. "Name, email" works, and so does a straight
          copy out of a spreadsheet. Nothing sends until you have seen the preview.
        </p>
      </div>

      {results ? (
        <ResultsPanel results={results} onCopy={copySummary}
          onReset={() => { setResults(null); setText(""); }} onBack={onBack} />
      ) : (
        <>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 16 }}>
            <div style={{ flex: "1 1 240px" }}>
              <label style={lbl}>Team</label>
              <select value={groupId} onChange={(e) => setGroupId(e.target.value)}
                style={{ ...inp, cursor: "pointer" }}>
                {teamIds.length === 0 && <option value="">No teams available</option>}
                {teamIds.map((id) => {
                  const c = capacityOf(st.people, st.groups, id);
                  return (
                    <option key={id} value={id}>
                      {st.groups[id].name} ({c.used}/{c.capacity})
                    </option>
                  );
                })}
              </select>
            </div>
            <div style={{ flex: "1 1 200px" }}>
              <label style={lbl}>Invite as</label>
              <select value={role} onChange={(e) => setRole(e.target.value)}
                style={{ ...inp, cursor: "pointer" }}>
                {grantable.map((r) => (
                  <option key={r} value={r}>{roleLabel(r)}</option>
                ))}
              </select>
              <div style={{ fontSize: 11.5, color: C.faint, marginTop: 5 }}>
                {ROLE_HINT[role] ?? ""}
              </div>
            </div>
          </div>

          <label style={lbl}>The men</label>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={9}
            placeholder={"Marcus Bell, marcus@example.com\nTroy Adams\ttroy@example.com\nSam Reyes <sam@example.com>"}
            style={{ ...inp, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: 13, lineHeight: 1.6, resize: "vertical" }} />

          {rows.length > 0 && (
            <>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap",
                margin: "16px 0 10px" }}>
                <div style={kicker}>Preview</div>
                <Pill label={`${willSend} to send`} tone={willSend ? "red" : "neutral"} />
                {rows.length - sendable.length > 0 && (
                  <Pill label={`${rows.length - sendable.length} skipped`} />
                )}
                {cap && <span style={{ fontSize: 12, color: C.sub }}>
                  {st.groups[groupId]?.name} has room for {cap.remaining} more
                </span>}
              </div>

              {overflow > 0 && (
                <Callout tone="warn">
                  {overflow} {overflow === 1 ? "man goes" : "men go"} past this team's
                  limit of {cap.capacity} and will not be sent. Raise the team's
                  capacity under Manage, or put them on another team.
                </Callout>
              )}

              <div style={{ border: `1px solid ${C.line}`, borderRadius: 12, overflow: "hidden" }}>
                {rows.map((r, i) => {
                  const past = r.valid && !r.duplicate && cap
                    && sendable.indexOf(r) >= cap.remaining;
                  return (
                    <div key={i} style={{ display: "flex", gap: 10, alignItems: "center",
                      padding: "9px 13px", fontSize: 13,
                      background: i % 2 ? "#FBFBFB" : C.panel,
                      color: r.problem || past ? C.faint : C.text }}>
                      <span style={{ width: 150, flexShrink: 0, fontWeight: 600,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {r.name || "—"}
                      </span>
                      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis",
                        whiteSpace: "nowrap" }}>{r.email}</span>
                      {r.problem
                        ? <Pill label={r.problem} />
                        : past ? <Pill label="Over the limit" />
                               : <Pill label="Ready" tone="red" />}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {err && <Callout tone="err">{err}</Callout>}

          <button onClick={send} disabled={sending || willSend === 0 || !groupId}
            style={{ ...primaryBtn, width: "100%", marginTop: 18, justifyContent: "center",
              padding: "13px", fontSize: 14,
              opacity: sending || willSend === 0 || !groupId ? 0.6 : 1 }}>
            <Send size={15} />
            {sending ? "Sending…" : willSend ? `Send ${willSend} invite${willSend === 1 ? "" : "s"}` : "Nothing to send"}
          </button>

          {willSend > 20 && (
            <div style={{ fontSize: 12, color: C.faint, textAlign: "center", marginTop: 10,
              lineHeight: 1.5 }}>
              Invite email is rate limited. If some fail with a limit message,
              wait an hour and use Retry — nothing is lost.
            </div>
          )}
        </>
      )}
    </div>
  );
}

const ROLE_HINT = {
  mentee: "Tracks his own goals.",
  mentor: "Can invite and suspend on this team.",
  admin: "Can run several teams.",
};

function Callout({ tone, children }) {
  const t = tone === "err"
    ? { bg: "rgba(213,0,50,0.06)", border: C.redLine, fg: C.redDk }
    : { bg: "rgba(200,136,28,0.10)", border: "rgba(200,136,28,0.35)", fg: "#9A6710" };
  return (
    <div style={{ display: "flex", gap: 9, alignItems: "flex-start", background: t.bg,
      border: `1px solid ${t.border}`, borderRadius: 10, padding: "12px 14px", margin: "12px 0" }}>
      <AlertTriangle size={15} color={t.fg} style={{ flexShrink: 0, marginTop: 1 }} />
      <div style={{ fontSize: 13, color: t.fg, lineHeight: 1.55 }}>{children}</div>
    </div>
  );
}

function ResultsPanel({ results, onCopy, onReset, onBack }) {
  const sent = results.filter((r) => r.ok);
  const failed = results.filter((r) => !r.ok);
  return (
    <div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
        marginBottom: 14 }}>
        <div style={{ ...display, fontSize: 22, color: C.text }}>
          {sent.length} invite{sent.length === 1 ? "" : "s"} sent
        </div>
        {failed.length > 0 && <Pill label={`${failed.length} failed`} tone="red" />}
      </div>

      <div style={{ border: `1px solid ${C.line}`, borderRadius: 12, overflow: "hidden" }}>
        {results.map((r, i) => (
          <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 13px",
            fontSize: 13, background: i % 2 ? "#FBFBFB" : C.panel }}>
            {r.ok
              ? <Check size={15} color="#1F6B40" style={{ flexShrink: 0 }} />
              : <AlertTriangle size={15} color={C.redDk} style={{ flexShrink: 0 }} />}
            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis",
              whiteSpace: "nowrap" }}>{r.email}</span>
            {!r.ok && (
              <span style={{ fontSize: 12, color: C.redDk, textAlign: "right", maxWidth: 260 }}>
                {r.error ?? r.code}
              </span>
            )}
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 18, flexWrap: "wrap" }}>
        <button onClick={onBack} style={primaryBtn}>Done</button>
        <button onClick={onReset} style={smallBtn}>
          <Users size={14} /> Invite more
        </button>
        <button onClick={onCopy} style={smallGhost}>
          <Copy size={14} /> Copy summary
        </button>
      </div>

      {failed.length > 0 && (
        <div style={{ fontSize: 12.5, color: C.sub, marginTop: 14, lineHeight: 1.6 }}>
          Copy the summary for your records. A man who already has an account
          will show as a duplicate — that is expected, not an error to chase.
        </div>
      )}
    </div>
  );
}
